import React, { ReactNode } from "react";
import { DateTime } from "luxon";
import { cn } from "../../../lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../components/ui/tooltip";
import { InfoIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { Calendar } from "../../../components/ui/calendar";

function DateField({
    date,
    ...props
}: {
    date: string | undefined;
    editable: boolean;
    placeholder: string;
    onDateSelected?: (date: string | undefined) => void;
    fromDate?: Date;
    toDate?: Date;
}) {
    const displayContent = (
        <div
            className={cn(
                "-mx-1.5 rounded-md px-1.5 py-0.5 font-medium transition-all",
                props.editable && "cursor-pointer text-primary hover:bg-primary/10"
            )}
        >
            {date ? dateFormat(date) : <div className={"opacity-45"}>{props.placeholder}</div>}
        </div>
    );

    const [open, setOpen] = React.useState(false);

    if (!props.editable) {
        return displayContent;
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{displayContent}</PopoverTrigger>
            <PopoverContent className="w-auto p-0">
                <Calendar
                    fromDate={props.fromDate ?? new Date()}
                    toDate={props.toDate}
                    mode="single"
                    defaultMonth={date ? new Date(date) : undefined}
                    selected={date ? new Date(date) : undefined}
                    onSelect={(s) => {
                        props.onDateSelected?.(s?.toISOString());
                        setOpen(false);
                    }}
                />
            </PopoverContent>
        </Popover>
    );
}

interface Props {
    from: string | undefined;
    to: string | undefined;
    editable?: boolean;
    infoTooltip?: ReactNode;
    onFromChange?: (date: string | undefined) => void;
    onToChange?: (date: string | undefined) => void;
}

export function PlannedTimeline(props: Props) {
    return (
        <div className={"flex flex-row items-center gap-2 text-sm"}>
            <div className={"font-medium text-muted-foreground"}>Planned timeline:</div>
            <DateField
                date={props.from}
                toDate={props.to ? new Date(props.to) : undefined}
                editable={props.editable ?? false}
                onDateSelected={(d) => props.onFromChange?.(d)}
                placeholder={"Start date"}
            />
            <div>to</div>
            <DateField
                date={props.to}
                fromDate={props.from ? new Date(props.from) : undefined}
                editable={props.editable ?? false}
                onDateSelected={(d) => props.onToChange?.(d)}
                placeholder={"End date"}
            />
            {props.infoTooltip && (
                <Tooltip delayDuration={100}>
                    <TooltipTrigger className={"cursor-help"}>
                        <InfoIcon strokeWidth={2.5} className={"h-4 w-4 text-muted-foreground"} />
                    </TooltipTrigger>
                    <TooltipContent>{props.infoTooltip}</TooltipContent>
                </Tooltip>
            )}
        </div>
    );
}

function dateFormat(date: string) {
    return DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED);
}
